import { useState } from "react";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { IndividualAdvancedSearchControl } from "@/Common/Components/Search/IndividualAdvancedSearchControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { SearchInput } from "../../Search/SearchInput";
import { PatientInfoBanner } from "./PatientInfoBanner";

import styles from "./PatientSearchMatchingPopup.module.scss";

interface IPatientSearchMatchingPopupProps {
    /** The title of the popup */
    title: string;

    /** The current patient of the main context */
    currentPatient: Patient;

    /** Function to call when the popup is closed (triggered by Cancel button or X) */
    onClose: () => void;

    /** Optional function to call when a patient is selected */
    onSelect?: (patient: Person) => void;

    /** Boolean to disable the main action button if true */
    disableActionButton?: boolean;

    /** Label for the main action button */
    actionButtonLabel: string;

    /** Function to call when the action button is clicked */
    onActionButtonClick: (patient: Person) => void;

    /** Optional error message */
    error?: string;

    /** Optional custom footer element */
    customFooter?: JSX.Element;

    /** Ignore patients that are already part of a merge request */
    ignorePatientsAlreadyPartOfMergeRequest?: boolean;
}

/**
 * This popup serves to search for patients and see which will match.
 * @param props IAutoMergeSelectingPopup
 * @returns JSX.Element
 */
function PatientSearchMatchingPopup(props: Readonly<IPatientSearchMatchingPopupProps>): JSX.Element {
    const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
    const [deactivate, setDeactivate] = useState<boolean>(false);
    const [searchString, setSearchString] = useState<string>("");

    const { getText } = useTranslation();

    return (
        <Modal
            title={props.title}
            showOverlay
            width={1000}
            top={20}
            showX
            disableOverlayClose
            onClose={() => props.onClose()}
            footer={props.customFooter
                ? props.customFooter
                : (
                    <div className={styles.footer}>
                        {props.error ? <p className={styles.invalid}>{props.error}</p> : <div />}
                        <ButtonGroup>
                            <ButtonControl
                                label={getText("CancelLabel")}
                                onClick={() => props.onClose()}
                                className="btn btn-default"
                            />
                            <ButtonControl
                                label={props.actionButtonLabel}
                                IsDisabled={!selectedPerson || props.disableActionButton}
                                className="btn btn-primary"
                                onClick={() => props.onActionButtonClick(selectedPerson)}
                            />
                        </ButtonGroup>
                    </div>
                )
            }
        >
            <div className={styles.patientMatchingSearchContainer}>
                <div className={styles.patientBannerPanel}>
                    <PatientInfoBanner
                        patient={props.currentPatient}
                        onHealthcardNumberClick={healthcardNumber => setSearchString(healthcardNumber)}
                        onPatientNameClick={name => setSearchString(name)}
                        onPatientPhoneNumberClick={phone => setSearchString(ValidationHelper.cleanPhoneNumber(phone.Number, phone.CountryLookupId))}
                    />
                </div>
                <div className={styles.searchPanel}>
                    <SearchInput
                        value={searchString}
                        onChange={e => setSearchString(e.target.value)}
                        className="margin-md-bottom"
                    />

                    <CheckboxControl
                        checked={deactivate}
                        onChange={isChecked => setDeactivate(isChecked)}
                        label={getText("DeactivatedFilesLabel")}
                    />

                    <IndividualAdvancedSearchControl
                        currentPersonContext={Person.FromPatient(props.currentPatient)}
                        resultCSS={{ maxHeight: "500px", overflowY: "auto" }}
                        defaultTextValue={searchString}
                        isFocusOnLoad={true}
                        componentId="_automerge-selection"
                        maxResultsToReturn={6}
                        onIndividualSelectCallback={person => {
                            setSelectedPerson(person);
                            props.onSelect?.(person);
                        }}
                        isRenderTextbox={false}
                        isIncludingInactiveIndividuals={deactivate}
                        isSearchingRelatives={false}
                        isSearchingPatients={true}
                        ignorePatientsAlreadyPartOfMergeRequest={props.ignorePatientsAlreadyPartOfMergeRequest}
                    />
                </div>
            </div>
        </Modal>
    );
}

export { PatientSearchMatchingPopup };
